<template>
  <div class="px-4 py-5 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-6xl space-y-6">
      <header class="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-bochas-rojo">Contenido del sitio</p>
          <h2 class="mt-1 text-2xl font-bold text-white sm:text-3xl">Alojamiento</h2>
          <p class="mt-2 max-w-2xl text-sm leading-relaxed text-bochas-text-muted">
            Administrá la portada, los espacios, sus comodidades, tarifas y datos de contacto.
          </p>
        </div>
        <router-link to="/alojamiento" target="_blank" class="preview-link">
          <span>Ver página pública</span>
          <IconFbochas name="arrow-right" :size="16" />
        </router-link>
      </header>

      <div v-if="loading" class="card-fbochas flex items-center gap-3 p-6 text-bochas-text-muted">
        <span class="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-bochas-rojo"></span>
        Cargando alojamiento...
      </div>
      <div v-else-if="loadError" class="card-fbochas space-y-4 p-6">
        <p role="alert" class="text-bochas-rojo">{{ loadError }}</p>
        <button type="button" class="btn-bochas-rojo px-5 py-2 text-sm" @click="load">Reintentar</button>
      </div>
      <form v-else @submit.prevent="save" @invalid.capture="$event.target.closest('details')?.setAttribute('open', '')">
        <fieldset :disabled="busy" class="min-w-0 space-y-4">
          <details v-for="(section, sectionIndex) in alojamientoSections" :key="section.label" :open="sectionIndex === 0" class="editor-section card-fbochas overflow-hidden">
            <summary class="editor-section__summary">
              <span class="editor-section__icon"><IconFbochas :name="sectionMeta[sectionIndex].icon" :size="20" /></span>
              <span class="min-w-0 flex-1">
                <span class="block text-base font-bold text-white sm:text-lg">{{ section.label }}</span>
                <span class="mt-0.5 block text-xs font-normal text-bochas-text-muted sm:text-sm">{{ sectionMeta[sectionIndex].description }}</span>
              </span>
              <IconFbochas name="chevron-down" :size="19" class="editor-section__chevron" />
            </summary>
            <div class="editor-section__content">
              <div v-for="field in section.fields" :key="field.key" class="field-group">
                <label :for="field.key" class="block text-sm font-medium text-white">{{ field.label }}</label>

                <template v-if="field.type === 'images' || field.type === 'image'">
                  <div class="grid gap-3 sm:grid-cols-2">
                    <article v-for="(url, index) in imageValues(field)" :key="index" class="image-card">
                      <div class="relative aspect-video overflow-hidden rounded-lg bg-black/30">
                        <img v-if="url" :src="url" :alt="`${field.label} ${index + 1}`" class="h-full w-full object-cover" />
                        <div v-else class="flex h-full items-center justify-center text-bochas-text-muted"><IconFbochas name="camara" :size="26" /></div>
                        <span v-if="field.type === 'images'" class="absolute left-2 top-2 rounded-full bg-black/65 px-2 py-1 text-[11px] font-semibold text-white">{{ index + 1 }}</span>
                      </div>
                      <details class="url-editor">
                        <summary>Usar URL de imagen</summary>
                        <input :id="index === 0 ? field.key : `${field.key}-${index}`" type="url" :value="url" :aria-label="`URL de ${field.label} ${index + 1}`" placeholder="https://..." class="admin-input mt-2" @input="setImage(field, index, $event.target.value)" />
                      </details>
                      <div class="flex flex-wrap items-center gap-2">
                        <label class="admin-secondary-button cursor-pointer">
                          <IconFbochas name="upload" :size="15" />
                          {{ url ? 'Cambiar' : 'Subir imagen' }}
                          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="sr-only" @change="upload(field, index, $event)" />
                        </label>
                        <template v-if="field.type === 'images'">
                          <button type="button" class="icon-button" :disabled="index === 0" :aria-label="`Mover imagen ${index + 1} antes`" title="Mover antes" @click="moveImage(field.key, index, -1)"><IconFbochas name="chevron-left" :size="16" /></button>
                          <button type="button" class="icon-button" :disabled="index === form[field.key].length - 1" :aria-label="`Mover imagen ${index + 1} después`" title="Mover después" @click="moveImage(field.key, index, 1)"><IconFbochas name="chevron-right" :size="16" /></button>
                        </template>
                        <button type="button" class="danger-button ml-auto" @click="field.type === 'images' ? form[field.key].splice(index, 1) : form[field.key] = ''"><IconFbochas name="trash" :size="14" /> Quitar</button>
                      </div>
                    </article>
                  </div>
                  <button v-if="field.type === 'images'" type="button" class="admin-secondary-button" @click="form[field.key].push('')">+ Agregar imagen</button>
                </template>

                <template v-else-if="field.type === 'list'">
                  <p class="text-xs text-bochas-text-muted">Escribí una característica por línea.</p>
                  <textarea :id="field.key" :value="form[field.key].join('\n')" rows="7" class="admin-input" @input="form[field.key] = $event.target.value.split('\n')"></textarea>
                </template>

                <template v-else-if="field.type === 'prices'">
                  <div v-for="(price, index) in form[field.key]" :key="index" class="price-card">
                    <label class="block text-sm text-white">Categoría
                      <input v-model="price.titulo" class="admin-input mt-2" required />
                    </label>
                    <div v-for="(item, itemIndex) in price.items" :key="itemIndex" class="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
                      <label class="text-sm text-white">Habitación o concepto<input v-model="item.label" class="admin-input mt-2" required /></label>
                      <label class="text-sm text-white">Precio<input v-model="item.valor" class="admin-input mt-2" required placeholder="$80.000" /></label>
                      <button type="button" class="danger-button mb-0.5" @click="price.items.splice(itemIndex, 1)"><IconFbochas name="trash" :size="14" /> Quitar</button>
                    </div>
                    <div class="flex flex-wrap gap-4">
                      <button type="button" class="admin-secondary-button" @click="price.items.push({ label: '', valor: '' })">+ Agregar tarifa</button>
                      <button type="button" class="danger-button" @click="form[field.key].splice(index, 1)"><IconFbochas name="trash" :size="14" /> Quitar categoría</button>
                    </div>
                  </div>
                  <button type="button" class="admin-secondary-button" @click="form[field.key].push({ titulo: '', items: [{ label: '', valor: '' }] })">+ Agregar categoría</button>
                </template>

                <RichTextEditor
                  v-else-if="field.type === 'textarea' && field.label === 'Descripción'"
                  v-model="form[field.key]"
                />
                <textarea v-else-if="field.type === 'textarea'" :id="field.key" v-model="form[field.key]" rows="4" class="admin-input"></textarea>
                <input v-else :id="field.key" v-model="form[field.key]" class="admin-input" />
              </div>
            </div>
          </details>
        </fieldset>
        <div class="save-bar sticky bottom-20 mt-6" :class="{ 'save-bar--dirty': dirty }">
          <p v-if="error" role="alert" class="mb-3 text-sm text-bochas-rojo">{{ error }}</p>
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex min-w-0 items-center gap-3" aria-live="polite">
              <span class="save-status" :class="{ 'save-status--dirty': dirty, 'save-status--saved': saved && !dirty }">
                <IconFbochas v-if="saved && !dirty" name="check" :size="15" />
              </span>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-white">
                  {{ uploading ? 'Subiendo imagen...' : dirty ? 'Cambios pendientes' : saved ? 'Cambios publicados' : 'Contenido actualizado' }}
                </p>
                <p class="mt-0.5 text-xs text-bochas-text-muted">
                  {{ dirty ? 'Publicalos para verlos en la página de Alojamiento.' : 'La página pública muestra la última versión guardada.' }}
                </p>
              </div>
            </div>
            <button type="submit" class="publish-button" :disabled="busy || !dirty">
              <span v-if="saving" class="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white"></span>
              <IconFbochas v-else name="check" :size="17" />
              {{ saving ? 'Publicando...' : 'Publicar cambios' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import RichTextEditor from "@/components/Common/RichTextEditor.vue";
import { alojamientoSections, crearAlojamientoContent, obtenerAlojamientoContent, guardarAlojamientoContent } from "@/services/siteSettings/alojamientoContentService";
import { subirSiteMediaImage } from "@/services/siteMedia/siteMediaService";

const router = useRouter();
const sectionMeta = [
  { icon: "camara", description: "Imagen, título y presentación principal." },
  { icon: "casa", description: "Información, comodidades, galería y tarifas." },
  { icon: "alojamiento", description: "Información para estudiantes, requisitos y precio." },
  { icon: "phoneCall", description: "Dirección y teléfonos visibles en el sitio." },
];
const form = ref(crearAlojamientoContent());
const original = ref(JSON.stringify(form.value));
const loading = ref(true);
const loadError = ref("");
const saving = ref(false);
const uploading = ref(false);
const error = ref("");
const saved = ref(false);
const busy = computed(() => saving.value || uploading.value);
const dirty = computed(() => JSON.stringify(form.value) !== original.value);

async function load() {
  loading.value = true;
  loadError.value = "";
  try {
    form.value = await obtenerAlojamientoContent();
    original.value = JSON.stringify(form.value);
  } catch (_) {
    loadError.value = "No se pudo cargar el alojamiento. Reintentá para editar el contenido actual.";
  } finally {
    loading.value = false;
  }
}

function imageValues(field) {
  return field.type === "images" ? form.value[field.key] : [form.value[field.key]];
}
function setImage(field, index, url) {
  if (field.type === "images") form.value[field.key][index] = url;
  else form.value[field.key] = url;
}
function moveImage(key, index, direction) {
  const list = form.value[key];
  const [item] = list.splice(index, 1);
  list.splice(index + direction, 0, item);
}
async function upload(field, index, event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file || busy.value) return;
  error.value = "";
  if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type) || file.size > 10 * 1024 * 1024) {
    error.value = "Elegí una imagen JPG, PNG, WebP o GIF de hasta 10 MB.";
    return;
  }
  uploading.value = true;
  try {
    // Cada carga usa una clave nueva para conservar las imágenes publicadas hasta guardar.
    const key = `alojamiento.upload.${crypto.randomUUID()}`;
    const result = await subirSiteMediaImage(key, file);
    const url = result.url || result.item?.imageUrl;
    if (!url) throw new Error("Sin URL");
    setImage(field, index, url);
  } catch (_) {
    error.value = "No se pudo subir la imagen. Intentá nuevamente.";
  } finally {
    uploading.value = false;
  }
}
async function save() {
  if (busy.value) return;
  saving.value = true;
  saved.value = false;
  error.value = "";
  try {
    const content = JSON.parse(JSON.stringify(form.value));
    for (const section of alojamientoSections) {
      for (const field of section.fields) {
        if (["list", "images"].includes(field.type)) content[field.key] = content[field.key].map(value => value.trim()).filter(Boolean);
      }
    }
    await guardarAlojamientoContent(content);
    form.value = content;
    original.value = JSON.stringify(content);
    saved.value = true;
  } catch (_) {
    error.value = "No se pudieron guardar los cambios. Tu edición se conserva para reintentar.";
  } finally {
    saving.value = false;
  }
}
function beforeUnload(event) {
  if (!dirty.value && !busy.value) return;
  event.preventDefault();
  event.returnValue = "";
}
const removeNavigationGuard = router.beforeEach((to, from) => {
  if (from.name !== "admin-alojamiento" || to.name === from.name) return true;
  if (busy.value) return false;
  return !dirty.value || window.confirm("Tenés cambios sin guardar. ¿Querés salir sin guardarlos?");
});
onMounted(() => {
  load();
  window.addEventListener("beforeunload", beforeUnload);
});
onBeforeUnmount(() => {
  removeNavigationGuard();
  window.removeEventListener("beforeunload", beforeUnload);
});
</script>

<style scoped>
.admin-input {
  @apply w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-bochas-rojo;
}

.editor-section {
  padding: 0;
}

.editor-section__summary {
  @apply flex cursor-pointer list-none items-center gap-3 px-4 py-4 sm:px-5;
}

.editor-section__summary::-webkit-details-marker {
  display: none;
}

.editor-section__icon {
  @apply flex h-10 w-10 flex-none items-center justify-center rounded-xl text-bochas-rojo;
  background-color: rgba(165, 33, 33, 0.15);
}

.editor-section__chevron {
  @apply flex-none text-white/50 transition-transform duration-200;
}

.editor-section[open] .editor-section__chevron {
  transform: rotate(180deg);
}

.editor-section__content {
  @apply space-y-6 border-t border-white/10 px-4 py-5 sm:px-5 sm:py-6;
}

.field-group {
  @apply space-y-2.5;
}

.image-card,
.price-card {
  @apply space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3;
}

.url-editor summary {
  @apply cursor-pointer list-none text-xs text-bochas-text-muted transition-colors hover:text-white;
}

.admin-secondary-button,
.icon-button,
.danger-button {
  @apply inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors;
}

.admin-secondary-button,
.icon-button {
  @apply border-white/10 bg-white/[0.06] text-white hover:bg-white/10;
}

.icon-button {
  @apply h-8 w-8 p-0 disabled:cursor-not-allowed disabled:opacity-30;
}

.danger-button {
  @apply border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20;
}

.preview-link {
  @apply inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10;
}

.save-bar {
  @apply rounded-2xl border border-white/10 bg-[#0a0e1a]/95 p-4 shadow-2xl backdrop-blur-xl transition-colors;
}

.save-bar--dirty {
  border-color: rgba(165, 33, 33, 0.55);
}

.save-status {
  @apply flex h-9 w-9 flex-none items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white;
}

.save-status--dirty {
  @apply border-amber-400/30 bg-amber-400/10;
}

.save-status--dirty::after {
  content: "";
  @apply h-2.5 w-2.5 rounded-full bg-amber-300;
}

.save-status--saved {
  @apply border-emerald-400/30 bg-emerald-400/10 text-emerald-300;
}

.publish-button {
  @apply inline-flex w-full flex-none items-center justify-center gap-2 rounded-xl bg-bochas-rojo px-6 py-3 text-sm font-bold text-white shadow-lg transition duration-200 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-bochas-rojo focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0e1a] disabled:cursor-not-allowed disabled:opacity-35 disabled:shadow-none sm:w-auto;
}
</style>
